import { Component, computed, effect, inject, signal, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { DatePickerModule } from 'primeng/datepicker';
import { TabsModule } from 'primeng/tabs';
import { PickListModule } from 'primeng/picklist';

import {
  ReportScheduleService,
  ReportScheduleDto,
  ReportScheduleRow,
  ReportRunLog,
  ReportScheduleOptions,
  ReportTemplate,
  ReportColumnDef,
} from '../../services/report-schedule';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';

type Opt = { label: string; value: string };

/** Run-log + report-type tag colors (spec). */
const RUN_SEVERITY: Record<string, 'success' | 'info' | 'warn' | 'danger'> = {
  SUCCESS: 'success',
  NO_DATA: 'info',
  RUNNING: 'warn',
  FAILED: 'danger',
};
const REPORT_SEVERITY: Record<string, 'info' | 'success'> = {
  COI_GENERATED: 'info',
  PAYMENTS: 'success',
};

/** Friendly labels for the backend's enum-ish codes; unknown codes fall back to Title Case. */
const LABELS: Record<string, string> = {
  COI_GENERATED: 'COI Generated',
  PAYMENTS: 'Payments',
  ALL: 'All',
  PARTNER: 'Partner',
  CLIENT: 'Client',
  SAME_DAY: 'Same day (today)',
  PREVIOUS_DAY: 'Previous day',
  LAST_7_DAYS: 'Last 7 days',
  MONTH_TO_DATE: 'Month to date',
  ALL_TIME: 'All time',
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

@Component({
  selector: 'app-report-scheduler',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TableModule,
    DialogModule,
    SelectModule,
    MultiSelectModule,
    AutoCompleteModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    InputTextModule,
    InputNumberModule,
    ToggleSwitchModule,
    DatePickerModule,
    TabsModule,
    PickListModule,
  ],
  templateUrl: './report-scheduler.html',
  styleUrls: ['./report-scheduler.css'],
})
export class ReportSchedulerPage implements OnInit {
  private fb = inject(FormBuilder);
  private svc = inject(ReportScheduleService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);
  readonly pageTitle = inject(PageTitleService).title;

  // ===================== tabs =====================
  activeTab = signal<'schedules' | 'templates'>('schedules');

  setTab(v: string | number | undefined) {
    const tab = v === 'templates' ? 'templates' : 'schedules';
    this.activeTab.set(tab);
    if (tab === 'templates') {
      this.ensureOptions(); // column catalogue drives the "n of m columns" cell
      if (!this.tplLoadedOnce) this.loadTemplates();
    }
  }

  // ===================== list =====================
  loading = signal(false);
  rows = signal<ReportScheduleRow[]>([]);
  /** id of the schedule currently doing a Run now (button spinner). */
  runningId = signal<number | null>(null);

  // ===================== options (loaded once, on first dialog open) =====================
  options = signal<ReportScheduleOptions | null>(null);
  optionsLoading = signal(false);

  reportTypeOptions = computed<Opt[]>(() => this.toOpts(this.options()?.reportTypes));
  scopeTypeOptions = computed<Opt[]>(() => this.toOpts(this.options()?.scopeTypes));
  windowOptions = computed<Opt[]>(() => this.toOpts(this.options()?.reportWindows));
  frequencyOptions = computed<Opt[]>(() => this.toOpts(this.options()?.frequencies));
  statusOptions = computed<Opt[]>(() => this.toOpts(this.options()?.paymentStatuses));
  dayOfWeekOptions = computed(() => this.options()?.daysOfWeek ?? []);
  partnerOptions = computed(() =>
    (this.options()?.partners ?? []).map((p) => ({
      label: p.code ? `${p.name} (${p.code})` : p.name,
      value: p.id,
    })),
  );
  clientOptions = computed(() =>
    (this.options()?.clients ?? []).map((c) => ({
      label: c.code ? `${c.name} (${c.code})` : c.name,
      value: c.id,
    })),
  );
  /** Templates of the currently selected report type + the default "all columns" choice. */
  templateOptions = computed<{ label: string; value: number | null }[]>(() => {
    const type = this.fReportType();
    return [
      { label: 'All columns (default)', value: null },
      ...(this.options()?.templates ?? [])
        .filter((t) => t.reportType === type)
        .map((t) => ({ label: t.templateName, value: t.id as number | null })),
    ];
  });

  // ===================== create / edit dialog =====================
  showModal = signal(false);
  editing = signal(false);
  saving = signal(false);
  private editId = 0;

  form: FormGroup = this.fb.group({
    scheduleName: ['', Validators.required],
    reportType: ['COI_GENERATED', Validators.required],
    templateId: [null as number | null], // must be declared AFTER reportType (reset clears it, see ctor)
    scopeType: ['ALL', Validators.required],
    partnerId: [null as number | null],
    clientId: [null as number | null],
    statusList: [[] as string[]], // PAYMENTS only; empty = all
    reportWindow: ['PREVIOUS_DAY', Validators.required],
    frequency: ['DAILY', Validators.required],
    // Holds a Date for the time-only picker; converted to 'HH:mm' on save (dateToTime).
    runAtTime: [null as Date | null, Validators.required],
    dayOfWeek: [1 as number | null],
    dayOfMonth: [1 as number | null],
    isEmail: [true],
    isWhatsApp: [false],
    emailList: [[] as string[]],
    waList: [[] as string[]],
    isActive: [true],
  });

  // Mirrors of the fields that drive conditional UI (zoneless-safe reactivity).
  fReportType = signal('COI_GENERATED');
  fScopeType = signal('ALL');
  fReportWindow = signal('PREVIOUS_DAY');
  fFrequency = signal('DAILY');
  fIsEmail = signal(true);
  fIsWhatsApp = signal(false);

  constructor() {
    this.form.get('reportType')!.valueChanges.subscribe((v) => {
      this.fReportType.set(v ?? '');
      // templates belong to one report type — a stale pick would apply the wrong columns
      this.form.get('templateId')!.setValue(null, { emitEvent: false });
    });
    this.form.get('scopeType')!.valueChanges.subscribe((v) => {
      this.fScopeType.set(v ?? 'ALL');
      // scope drives which entity select shows — clear the other one
      if (v !== 'PARTNER') this.form.get('partnerId')!.setValue(null, { emitEvent: false });
      if (v !== 'CLIENT') this.form.get('clientId')!.setValue(null, { emitEvent: false });
    });
    this.form.get('reportWindow')!.valueChanges.subscribe((v) => this.fReportWindow.set(v ?? ''));
    this.form.get('frequency')!.valueChanges.subscribe((v) => this.fFrequency.set(v ?? 'DAILY'));
    this.form.get('isEmail')!.valueChanges.subscribe((v) => this.fIsEmail.set(!!v));
    this.form.get('isWhatsApp')!.valueChanges.subscribe((v) => this.fIsWhatsApp.set(!!v));

    // Template dialog: switching report type swaps the column catalogue — start the pick over.
    this.tplForm.get('reportType')!.valueChanges.subscribe((v) => {
      this.pendingTplKeys = [];
      this.rebuildTplPicker(v ?? 'COI_GENERATED', []);
    });

    // Options can still be in flight when the template dialog opens (ensureOptions is lazy) —
    // build the pick list as soon as the catalogue lands. Idempotent, so double-runs are fine.
    effect(() => {
      const opts = this.options();
      if (opts && this.showTplModal()) {
        this.rebuildTplPicker(this.tplForm.getRawValue().reportType, this.pendingTplKeys);
      }
    });
  }

  // ===================== logs dialog =====================
  showLogs = signal(false);
  logsLoading = signal(false);
  logsRows = signal<ReportRunLog[]>([]);
  /** Schedule the logs are filtered to; null = all schedules. */
  logsFor = signal<ReportScheduleRow | null>(null);

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) this.load();
  }

  // ===================== list =====================
  load() {
    this.loading.set(true);
    this.svc.getAll().subscribe({
      next: (res: any) => {
        this.loading.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to load schedules');
          return;
        }
        this.rows.set(Array.isArray(res?.data) ? res.data : []);
      },
      error: () => this.loading.set(false),
    });
  }

  // ===================== display helpers =====================
  label(code?: string | null): string {
    if (!code) return '—';
    return (
      LABELS[code] ??
      code
        .toLowerCase()
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
    );
  }

  reportSeverity(t?: string | null) {
    return REPORT_SEVERITY[(t || '').toUpperCase()] ?? 'secondary';
  }

  runSeverity(s?: string | null) {
    return RUN_SEVERITY[(s || '').toUpperCase()] ?? 'secondary';
  }

  scopeLabel(r: ReportScheduleRow): string {
    if (r.scopeType === 'PARTNER') return r.partnerName || `Partner #${r.partnerId}`;
    if (r.scopeType === 'CLIENT') return r.clientName || `Client #${r.clientId}`;
    return 'All';
  }

  /** DAILY 08:00 / WEEKLY Monday 08:00 / MONTHLY day 5 08:00 (IST). */
  freqLabel(r: ReportScheduleRow): string {
    const time = r.runAtTime || '';
    if (r.frequency === 'WEEKLY') {
      const day = this.dayOfWeekOptions().find((d) => d.value === r.dayOfWeek)?.label
        ?? ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][r.dayOfWeek ?? 0]
        ?? '';
      return `Weekly · ${day} ${time}`;
    }
    if (r.frequency === 'MONTHLY') return `Monthly · day ${r.dayOfMonth} · ${time}`;
    return `Daily · ${time}`;
  }

  recipientCount(csv?: string | null): number {
    return (csv || '').split(',').filter((s) => s.trim()).length;
  }

  /**
   * UTC → IST display. The API returns UTC both WITH a trailing 'Z' (Insert/Update/Enable)
   * and WITHOUT it (GetAll/Logs) — never trust local-time parsing for the no-Z form.
   */
  ist(s?: string | null): string {
    if (!s) return '—';
    const iso = /([zZ]|[+-]\d{2}:?\d{2})$/.test(s) ? s : s + 'Z';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  }

  // ===================== options =====================
  private ensureOptions() {
    if (this.options() || this.optionsLoading()) return;
    this.optionsLoading.set(true);
    this.svc.options().subscribe({
      next: (res: any) => {
        this.optionsLoading.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to load options');
          return;
        }
        this.options.set(res?.data ?? null);
      },
      error: () => this.optionsLoading.set(false),
    });
  }

  private toOpts(codes?: string[]): Opt[] {
    return (codes ?? []).map((c) => ({ label: this.label(c), value: c }));
  }

  // ===================== create / edit =====================
  openAdd() {
    this.ensureOptions();
    this.editing.set(false);
    this.editId = 0;
    this.form.reset({
      scheduleName: '',
      reportType: 'COI_GENERATED',
      templateId: null,
      scopeType: 'ALL',
      partnerId: null,
      clientId: null,
      statusList: [],
      reportWindow: 'PREVIOUS_DAY',
      frequency: 'DAILY',
      runAtTime: this.timeToDate('08:00'),
      dayOfWeek: 1,
      dayOfMonth: 1,
      isEmail: true,
      isWhatsApp: false,
      emailList: [],
      waList: [],
      isActive: true,
    });
    this.showModal.set(true);
  }

  openEdit(r: ReportScheduleRow) {
    this.ensureOptions();
    this.editing.set(true);
    this.editId = r.id;
    this.form.reset({
      scheduleName: r.scheduleName,
      reportType: r.reportType,
      templateId: r.templateId ?? null,
      scopeType: r.scopeType || 'ALL',
      partnerId: r.partnerId ?? null,
      clientId: r.clientId ?? null,
      statusList: this.splitCsv(r.paymentStatuses),
      reportWindow: r.reportWindow,
      frequency: r.frequency,
      runAtTime: this.timeToDate(r.runAtTime),
      dayOfWeek: r.dayOfWeek ?? 1,
      dayOfMonth: r.dayOfMonth ?? 1,
      isEmail: r.isEmail,
      isWhatsApp: r.isWhatsApp,
      emailList: this.splitCsv(r.emails),
      waList: this.splitCsv(r.whatsAppNumbers),
      isActive: r.isActive,
    });
    this.showModal.set(true);
  }

  // ===================== run-time <-> Date (time-only picker) =====================

  /** 'HH:mm' (or 'HH:mm:ss') IST wall time → Date for the picker; blank/invalid falls back to 08:00. */
  private timeToDate(t?: string | null): Date {
    const m = /^(\d{1,2}):(\d{2})/.exec((t || '').trim());
    const d = new Date();
    d.setHours(m ? +m[1] : 8, m ? +m[2] : 0, 0, 0);
    return d;
  }

  /** Picker Date → the 'HH:mm' 24h string the API stores; '' when not a valid Date. */
  private dateToTime(d: any): string {
    if (!(d instanceof Date) || isNaN(d.getTime())) return '';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  closeModal() {
    this.showModal.set(false);
  }

  private splitCsv(csv?: string | null): string[] {
    return (csv || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  /** All the conditional rules the backend enforces — checked here for instant feedback. */
  private validate(): string | null {
    const v = this.form.value;
    if (!v.scheduleName?.trim()) return 'Schedule name is required.';
    // TIME_RE safety net on the CONVERTED value — the picker holds a Date, the API a 'HH:mm' string.
    if (!TIME_RE.test(this.dateToTime(v.runAtTime))) return 'Pick the run time (IST).';
    if (v.scopeType === 'PARTNER' && !v.partnerId) return 'Select a partner for a partner-scoped report.';
    if (v.scopeType === 'CLIENT' && !v.clientId) return 'Select a client for a client-scoped report.';
    if (v.frequency === 'WEEKLY' && !(v.dayOfWeek >= 1 && v.dayOfWeek <= 7))
      return 'Pick a day of week for a weekly schedule.';
    if (v.frequency === 'MONTHLY' && !(v.dayOfMonth >= 1 && v.dayOfMonth <= 28))
      return 'Day of month must be 1–28 (so every month has the day).';
    if (!v.isEmail && !v.isWhatsApp) return 'Enable at least one delivery channel (email or WhatsApp).';

    if (v.isEmail) {
      const emails: string[] = (v.emailList ?? []).map((e: string) => e.trim()).filter(Boolean);
      if (!emails.length) return 'Add at least one email recipient (press Enter after typing).';
      const bad = emails.find((e) => !EMAIL_RE.test(e));
      if (bad) return `"${bad}" is not a valid email address.`;
      if (emails.join(',').length > 1000) return 'Email list is too long (max 1000 characters).';
    }
    if (v.isWhatsApp) {
      const nums: string[] = (v.waList ?? []).map((n: string) => n.replace(/\D/g, '')).filter(Boolean);
      if (!nums.length) return 'Add at least one WhatsApp number (press Enter after typing).';
      const bad = nums.find((n) => n.length < 10 || n.length > 14);
      if (bad) return `"${bad}" is not a valid WhatsApp number (10–14 digits).`;
      if (nums.join(',').length > 500) return 'WhatsApp number list is too long (max 500 characters).';
    }
    return null;
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
    }
    const err = this.validate();
    if (err) {
      this.toast.warning(err);
      return;
    }
    const v = this.form.value;
    const dto: ReportScheduleDto = {
      id: this.editId,
      scheduleName: v.scheduleName.trim(),
      reportType: v.reportType,
      templateId: v.templateId ?? null,
      scopeType: v.scopeType,
      partnerId: v.scopeType === 'PARTNER' ? v.partnerId : null,
      clientId: v.scopeType === 'CLIENT' ? v.clientId : null,
      paymentStatuses:
        v.reportType === 'PAYMENTS' && (v.statusList ?? []).length ? v.statusList.join(',') : null,
      reportWindow: v.reportWindow,
      frequency: v.frequency,
      runAtTime: this.dateToTime(v.runAtTime),
      dayOfWeek: v.frequency === 'WEEKLY' ? v.dayOfWeek : null,
      dayOfMonth: v.frequency === 'MONTHLY' ? v.dayOfMonth : null,
      isEmail: !!v.isEmail,
      isWhatsApp: !!v.isWhatsApp,
      emails: v.isEmail ? (v.emailList ?? []).map((e: string) => e.trim()).filter(Boolean).join(',') : null,
      whatsAppNumbers: v.isWhatsApp
        ? (v.waList ?? []).map((n: string) => n.replace(/\D/g, '')).filter(Boolean).join(',')
        : null,
      isActive: !!v.isActive,
    };

    this.saving.set(true);
    const req = this.editId ? this.svc.update(dto) : this.svc.insert(dto);
    req.subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to save schedule');
          return;
        }
        const next = res?.data?.nextRunAt ?? res?.data?.NextRunAt;
        this.toast.success(
          next ? `Schedule saved. Next run: ${this.ist(next)} IST` : 'Schedule saved',
        );
        this.closeModal();
        this.load();
      },
      error: () => this.saving.set(false),
    });
  }

  // ===================== enable / disable =====================
  toggleActive(r: ReportScheduleRow, on: boolean) {
    const req = on ? this.svc.enable(r.id) : this.svc.disable(r.id);
    req.subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to update schedule');
          this.load(); // revert the switch
          return;
        }
        if (on) {
          const next = res?.data?.nextRunAt ?? res?.data?.NextRunAt;
          this.toast.success(next ? `Enabled. Next run: ${this.ist(next)} IST` : 'Schedule enabled');
        } else {
          this.toast.info('Schedule paused — history is kept.');
        }
        this.load();
      },
      error: () => this.load(),
    });
  }

  // ===================== run now =====================
  async runNow(r: ReportScheduleRow) {
    const ok = await this.toast.confirm(
      `"${r.scheduleName}" will run immediately and deliver to everyone configured${r.isActive ? '' : ' (schedule is paused — this is a one-off run)'}.`,
      'Run report now?',
      'Run now',
    );
    if (!ok) return;
    this.runningId.set(r.id);
    this.svc.runNow(r.id).subscribe({
      next: (res: any) => {
        this.runningId.set(null);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Run failed');
          return;
        }
        // Delivery is queued (worker sends within ~30 s) — the message already says so.
        this.toast.popup(res?.message || 'Report generated and queued for delivery.', 'Run complete', 'success');
        this.load();
        if (this.showLogs()) this.loadLogs();
      },
      error: () => this.runningId.set(null),
    });
  }

  // ===================== delete =====================
  async remove(r: ReportScheduleRow) {
    const ok = await this.toast.confirm(
      `"${r.scheduleName}" will be deleted. Past run logs are kept.`,
      'Delete schedule?',
      'Delete',
    );
    if (!ok) return;
    this.svc.delete(r.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete');
          return;
        }
        this.toast.success('Schedule deleted');
        this.load();
      },
    });
  }

  /** id of the run whose Excel is currently downloading (button spinner). */
  downloadingId = signal<number | null>(null);

  /**
   * Download a run's Excel through the API (auth'd, streamed by storage key) rather than
   * opening the stored presigned link — that link expires with the IAM-role token and, for
   * pre-migration rows, points at a deleted bucket (S3 "NoSuchBucket"). SSR-safe.
   */
  async downloadExcel(l: ReportRunLog) {
    if (!isPlatformBrowser(this.platformId) || this.downloadingId() !== null) return;
    this.downloadingId.set(l.id);
    try {
      const blob = await firstValueFrom(this.svc.downloadFile(l.id));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(l.scheduleName || 'report').replace(/[^\w-]/g, '-')}-${l.id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      // Defer cleanup off the click tick so Firefox/Safari finish reading the blob.
      setTimeout(() => {
        a.remove();
        URL.revokeObjectURL(url);
      }, 0);
    } catch (err: any) {
      const msg = await this.readBlobError(err);
      this.toast.error(msg || 'Could not download the report file.');
    } finally {
      this.downloadingId.set(null);
    }
  }

  /** The DownloadFile endpoint returns its error envelope as a Blob (responseType:'blob'). */
  private async readBlobError(err: any): Promise<string> {
    try {
      const body = err?.error;
      if (body instanceof Blob) {
        const text = await body.text();
        try {
          return JSON.parse(text)?.message ?? text;
        } catch {
          return text;
        }
      }
      return body?.message ?? err?.message ?? '';
    } catch {
      return '';
    }
  }

  // ===================== logs =====================
  openLogs(r: ReportScheduleRow | null) {
    this.logsFor.set(r);
    this.logsRows.set([]);
    this.showLogs.set(true);
    this.loadLogs();
  }

  loadLogs() {
    this.logsLoading.set(true);
    this.svc.logs(this.logsFor()?.id, 50).subscribe({
      next: (res: any) => {
        this.logsLoading.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to load run logs');
          return;
        }
        this.logsRows.set(Array.isArray(res?.data) ? res.data : []);
      },
      error: () => this.logsLoading.set(false),
    });
  }

  // ===================== column templates (Templates tab) =====================
  tplRows = signal<ReportTemplate[]>([]);
  tplLoading = signal(false);
  private tplLoadedOnce = false;

  showTplModal = signal(false);
  tplEditing = signal(false);
  tplSaving = signal(false);
  private tplEditId = 0;
  /** >0 locks the report type — changing it would orphan the schedules using the template. */
  tplUsedBy = signal(0);

  readonly tplReportTypeOptions: Opt[] = [
    { label: 'COI / Members', value: 'COI_GENERATED' },
    { label: 'Payments', value: 'PAYMENTS' },
  ];

  tplForm: FormGroup = this.fb.group({
    templateName: ['', Validators.required],
    reportType: ['COI_GENERATED', Validators.required],
    isActive: [true],
  });

  /**
   * Live arrays for the p-pickList — the component mutates them in place (splice/push),
   * so the signals only swap references on rebuild; reading tplTarget() at save time
   * always reflects the user's current order.
   */
  tplSource = signal<ReportColumnDef[]>([]);
  tplTarget = signal<ReportColumnDef[]>([]);
  /** CSV keys of the template being edited, kept until Options (the catalogue) arrive. */
  private pendingTplKeys: string[] = [];

  loadTemplates() {
    this.tplLoading.set(true);
    this.svc.getTemplates().subscribe({
      next: (res: any) => {
        this.tplLoading.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to load templates');
          return;
        }
        this.tplLoadedOnce = true;
        this.tplRows.set(Array.isArray(res?.data) ? res.data : []);
      },
      error: () => this.tplLoading.set(false),
    });
  }

  /** "8 of 16 columns" — selected count vs the report type's full catalogue. */
  tplColumnsLabel(t: ReportTemplate): string {
    const selected = this.splitCsv(t.columns).length;
    const total = this.options()?.reportColumns?.[t.reportType]?.length ?? 0;
    return total ? `${selected} of ${total} columns` : `${selected} columns`;
  }

  openTplAdd() {
    this.ensureOptions();
    this.tplEditing.set(false);
    this.tplEditId = 0;
    this.tplUsedBy.set(0);
    this.tplForm.get('reportType')!.enable({ emitEvent: false });
    this.tplForm.reset({ templateName: '', reportType: 'COI_GENERATED', isActive: true });
    this.pendingTplKeys = [];
    this.rebuildTplPicker('COI_GENERATED', []);
    this.showTplModal.set(true);
  }

  openTplEdit(t: ReportTemplate) {
    this.ensureOptions();
    this.tplEditing.set(true);
    this.tplEditId = t.id;
    this.tplUsedBy.set(t.usedBySchedules || 0);
    const typeCtl = this.tplForm.get('reportType')!;
    if (t.usedBySchedules > 0) typeCtl.disable({ emitEvent: false });
    else typeCtl.enable({ emitEvent: false });
    // reset first — its reportType valueChanges wipes the picker, so rebuild AFTER it
    this.tplForm.reset({ templateName: t.templateName, reportType: t.reportType, isActive: t.isActive });
    this.pendingTplKeys = this.splitCsv(t.columns);
    this.rebuildTplPicker(t.reportType, this.pendingTplKeys);
    this.showTplModal.set(true);
  }

  closeTplModal() {
    this.showTplModal.set(false);
  }

  /** target = defs for selectedKeys in CSV order; source = catalogue minus selected. */
  private rebuildTplPicker(reportType: string, selectedKeys: string[]) {
    const catalogue = this.options()?.reportColumns?.[reportType] ?? [];
    const byKey = new Map(catalogue.map((c) => [c.key, c]));
    const target = selectedKeys
      .map((k) => byKey.get(k))
      .filter((c): c is ReportColumnDef => !!c);
    const chosen = new Set(target.map((c) => c.key));
    // fresh arrays every rebuild — the pickList splices them in place afterwards
    this.tplTarget.set(target);
    this.tplSource.set(catalogue.filter((c) => !chosen.has(c.key)));
  }

  saveTpl() {
    // getRawValue — reportType is disabled (and thus absent from .value) when in use
    const v = this.tplForm.getRawValue();
    if (!v.templateName?.trim()) {
      this.tplForm.markAllAsTouched();
      this.toast.warning('Template name is required.');
      return;
    }
    const columns = this.tplTarget().map((c) => c.key);
    if (!columns.length) {
      this.toast.warning('Move at least one column into "In the Excel".');
      return;
    }
    this.tplSaving.set(true);
    this.svc
      .saveTemplate({
        id: this.tplEditId,
        templateName: v.templateName.trim(),
        reportType: v.reportType,
        columns,
        isActive: !!v.isActive,
      })
      .subscribe({
        next: (res: any) => {
          this.tplSaving.set(false);
          if (res?.success === false) {
            // duplicate-name 409 / bad-columns 400 message verbatim; dialog stays open to fix
            this.toast.error(res?.message || 'Failed to save template');
            return;
          }
          this.toast.success('Template saved');
          this.closeTplModal();
          this.loadTemplates();
          this.reloadOptions(); // schedule dialog's template list must see the change
        },
        error: () => this.tplSaving.set(false),
      });
  }

  async removeTpl(t: ReportTemplate) {
    const ok = await this.toast.confirm(
      `"${t.templateName}" will be deleted.${
        t.usedBySchedules ? ` It is currently used by ${t.usedBySchedules} schedule(s).` : ''
      }`,
      'Delete template?',
      'Delete',
    );
    if (!ok) return;
    this.svc.deleteTemplate(t.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          // "used by schedules" 409 message verbatim
          this.toast.error(res?.message || 'Failed to delete template');
          return;
        }
        this.toast.success('Template deleted');
        this.loadTemplates();
        this.reloadOptions();
      },
    });
  }

  /** Drop the cached Options and re-fetch — template add/edit/delete changes its `templates` list. */
  private reloadOptions() {
    this.options.set(null);
    this.ensureOptions();
  }
}
